/**
 * V85 — campos fiscales del RECEPTOR (RFC, razón social, régimen, uso CFDI, CP)
 * COMPARTIDOS entre Configuración (FiscalDataCard) y el SubscribeDialog: una
 * sola fuente de labels/catálogos; la validación real es server-side
 * (UpsertFiscalDataDto vía PUT /me/fiscal-data) y no se duplica.
 */
import { api } from '../lib/api';

export const REGIMEN_LABELS: Record<string, string> = {
  '601': '601 — General de Ley Personas Morales',
  '603': '603 — Personas Morales con Fines no Lucrativos',
  '605': '605 — Sueldos y Salarios',
  '606': '606 — Arrendamiento',
  '612': '612 — Personas Físicas con Actividades Empresariales y Profesionales',
  '620': '620 — Sociedades Cooperativas de Producción',
  '621': '621 — Incorporación Fiscal',
  '622': '622 — Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras',
  '623': '623 — Opcional para Grupos de Sociedades',
  '624': '624 — Coordinados',
  '625': '625 — Actividades Empresariales por Plataformas Tecnológicas',
  '626': '626 — Régimen Simplificado de Confianza (RESICO)',
};

export const USO_LABELS: Record<string, string> = {
  G01: 'G01 — Adquisición de mercancías',
  G03: 'G03 — Gastos en general',
  I08: 'I08 — Otra maquinaria y equipo',
  S01: 'S01 — Sin efectos fiscales',
};

export interface FiscalData {
  rfc: string;
  razonSocial: string;
  regimenFiscal: string;
  usoCfdi: string;
  cp: string;
}

export interface FiscalDataResponse {
  complete: boolean;
  /** V85: TRUE = quiere factura, FALSE = NO explícito, null = nunca preguntado. */
  wantsCfdi: boolean | null;
  data: (FiscalData & { updatedAt: string }) | null;
  catalogs: { regimenesFiscales: string[]; usosCfdi: string[] };
}

export const EMPTY_FISCAL: FiscalData = {
  rfc: '', razonSocial: '', regimenFiscal: '601', usoCfdi: 'G03', cp: '',
};

export const getFiscalData = () => api<FiscalDataResponse>('/me/fiscal-data');

export const saveFiscalData = (form: FiscalData) =>
  api('/me/fiscal-data', {
    method: 'PUT',
    body: { ...form, rfc: form.rfc.trim().toUpperCase(), cp: form.cp.trim() },
  });

/** false = "no quiero factura" (checkout sin fricción; invoices → opted_out). */
export const saveCfdiOptin = (wantsCfdi: boolean) =>
  api('/me/fiscal-data/optin', { method: 'PUT', body: { wantsCfdi } });

export const fiscalErrMsg = (e: any, fallback: string): string => {
  const msgs = e?.body?.message;
  return Array.isArray(msgs) ? msgs.join(' ') : msgs || e?.message || fallback;
};

interface FieldsProps {
  form: FiscalData;
  onChange: (form: FiscalData) => void;
  catalogs?: FiscalDataResponse['catalogs'] | null;
  /** Layout compacto (SubscribeDialog) vs cómodo (Configuración). */
  compact?: boolean;
}

export function FiscalDataFields({ form, onChange, catalogs, compact }: FieldsProps) {
  const set = (k: keyof FiscalData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      onChange({ ...form, [k]: e.target.value });

  const inputCls = `w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none ${compact ? 'py-1.5' : 'py-2'}`;
  const labelCls = 'text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400';

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 ${compact ? 'gap-2.5' : 'gap-4'}`}>
      <div>
        <label className={labelCls}>RFC</label>
        <input value={form.rfc} onChange={set('rfc')} placeholder="AAA010101AAA"
          maxLength={13} className={`${inputCls} font-mono uppercase`} />
      </div>
      <div>
        <label className={labelCls}>Código postal (domicilio fiscal)</label>
        <input value={form.cp} onChange={set('cp')} placeholder="00000" maxLength={5}
          inputMode="numeric" className={`${inputCls} font-mono`} />
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>Razón social</label>
        <input value={form.razonSocial} onChange={set('razonSocial')}
          placeholder="Sin régimen societario (sin S.A. de C.V.)" className={inputCls} />
        <p className="mt-1 text-[11px] text-slate-400">
          Tal como aparece en tu Constancia, <strong>sin</strong> el régimen societario (S.A. de C.V., S. de R.L., …).
        </p>
      </div>
      <div>
        <label className={labelCls}>Régimen fiscal</label>
        <select value={form.regimenFiscal} onChange={set('regimenFiscal')} className={inputCls}>
          {(catalogs?.regimenesFiscales ?? Object.keys(REGIMEN_LABELS)).map((c) => (
            <option key={c} value={c}>{REGIMEN_LABELS[c] ?? c}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>Uso de CFDI</label>
        <select value={form.usoCfdi} onChange={set('usoCfdi')} className={inputCls}>
          {(catalogs?.usosCfdi ?? Object.keys(USO_LABELS)).map((c) => (
            <option key={c} value={c}>{USO_LABELS[c] ?? c}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
